import React, { useEffect, useState } from "react";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import CustomButton from "components/common/Button/CustomButton";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import moment from "moment";
import { apiGetRequest, apiPutRequest } from "helpers/Requests";
import {
  URL_ROSTERING_GET_OPEN_SHIFTS,
  URL_ROSTERING_APPROVE_OPEN_SHIFTS,
} from "store/services/URL";
import { toast } from "react-toastify";
import PaginationComponent from "components/common/Pagination/index";
import { emptyPaginationObj } from "Constants/constant";
import AvatarGroup from "components/common/Avatar/AvatarGroup";

import {
  baseURLImage,
  capitalizeWords,
  getNameProfile,
  truncateTextReporting,
} from "Utils/utils";
import { useSelector } from "react-redux";
import { getSpSetting } from "store/selector/sp.selector";
import { getParticipantLabel } from "Constants/constant";

const OpenShiftModal = ({
  isShow,
  handleClose,
  isUserGracePeriodOver,
  onShiftCountUpdate,
  calendarView,
}) => {
  const settings = useSelector((state) => getSpSetting(state));
  const participantLabelPlural = getParticipantLabel(settings?.care_sector, true) || "Participant(s)";

  const [isLoading, setIsLoading] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [selectedRequestUuid, setSelectedRequestUuid] = useState({});
  const [approvingShiftId, setApprovingShiftId] = useState(null);
  const [pagination, setPagination] = useState(emptyPaginationObj);
  const [page, setPage] = useState(1);

  const handleImageError = (e, fname, lname) => {
    return (e.target.src = getNameProfile(fname, lname));
  };

  useEffect(() => {
    if (isShow) {
      fetchOpenShifts();
    }
  }, [isShow, page, calendarView]);

  const fetchOpenShifts = async () => {
    setIsLoading(true);
    try {
      const view = calendarView === "worker-view" ? "worker" : "participant";
      const params = new URLSearchParams({
        status: "pending",
        page: page,
        per_page: 5,
        view: view,
      });
      const response = await apiGetRequest(
        `${URL_ROSTERING_GET_OPEN_SHIFTS}?${params.toString()}`
      );

      const shiftData = response?.data?.data?.data || [];
      const pagination = response?.data?.data?.pagination || {};

      setShifts(shiftData);

      setPagination({
        current_page: pagination?.current_page || 1,
        total: pagination?.total || 0,
        per_page: pagination?.per_page || params?.per_page,
        last_page: pagination?.last_page || 1,
      });

      if (onShiftCountUpdate) {
        onShiftCountUpdate(pagination?.total || 0 );
      }
    } catch (error) {
      // toast.error(error?.message ?? "Something went wrong");
    }
    setIsLoading(false);
  };

  const handleWorkerSelect = (shiftId, uuid) => {
    setSelectedRequestUuid({
      ...selectedRequestUuid,
      [shiftId]: uuid,
    });
  };

  const handleApprove = async (shift) => {
    const shiftId = shift?.shift_id;
    let selectedWorkerId = selectedRequestUuid[shiftId];

    if (!selectedWorkerId && shift?.requested_by?.length === 1) {
      selectedWorkerId = shift.requested_by[0]?.uuid;
    }

    if (!selectedWorkerId) {
      toast.error("Please select a worker to approve this shift");
      return;
    }

    setApprovingShiftId(shiftId);

    try {
      const endPoint = `${URL_ROSTERING_APPROVE_OPEN_SHIFTS}${selectedWorkerId}`;
      await apiPutRequest(endPoint);
      toast.success("Shift approved successfully!");

      await fetchOpenShifts();

      setSelectedRequestUuid({
        ...selectedRequestUuid,
        [shiftId]: "",
      });
    } catch (error) {
      toast.error(error?.message ?? "Failed to approve shift");
    } finally {
      setApprovingShiftId(null);
    }
  };

  const renderTableContent = () => (
    <div className="p-4">
      <h4 className="text-lg font-medium mb-4 px-2">
        <b>Total Shift Requests :</b> {pagination?.total}
      </h4>
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full border-collapse">
          <thead className="bg-blue-50 border-b sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                {participantLabelPlural}
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                Shift Date
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                Start Time
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                End Time
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                Duration
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                Worker Requests
              </th>
              <th className="text-left text-sm font-medium text-gray-700 pl-2 px-4 py-3">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {shifts?.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-red-500">
                  No open shift requests available
                </td>
              </tr>
            ) : (
              shifts?.map((shift) => {
                const shiftId = shift?.shift_id;
                return (
                  <tr key={shiftId} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        {shift?.participants?.length > 0 &&
                        shift?.participants?.length === 1 ? (
                          <div
                            className={`flex items-center gap-1`}
                            title={capitalizeWords(`${shift?.participants[0]?.first_name} ${shift?.participants[0]?.last_name}`)}
                          >
                            <div className="flex items-center gap-2">
                              <img
                                onError={(e) =>
                                  handleImageError(
                                    e,
                                    `${shift?.participants[0]?.first_name}`,
                                    `${shift?.participants[0]?.last_name}`
                                  )
                                }
                                className="relative z-10 inline-block h-6 w-6 rounded-full"
                                src={
                                  shift?.participants[0]?.profile_picture
                                    ? baseURLImage(
                                        shift?.participants[0]?.profile_picture
                                      )
                                    : getNameProfile(
                                        shift?.participants[0]?.first_name,
                                        shift?.participants[0]?.last_name
                                      )
                                }
                                alt=""
                              />

                            {truncateTextReporting(
                              capitalizeWords(
                                `${shift?.participants[0]?.first_name} ${shift?.participants[0]?.last_name}`,
                                10
                              )
                            )}
                             </div>
                          </div>
                        ) : (
                          <AvatarGroup
                            type="participant"
                            arrayData={shift?.participants || []}
                            cssClass="mt-0"
                            maxLength={3}
                            isNoRing={true}
                          />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {shift?.shift_date
                        ? moment(shift?.shift_date).format("DD/MM/YYYY")
                        : "-"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {shift?.start_time
                        ? moment(shift?.start_time).format("h:mm A")
                        : "-"}
                      {shift?.claim_type === "open_slot" && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          Open slot request
                        </div>
                      )}
                      {shift?.claim_type === "whole_open" && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          Whole open shift
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {shift?.end_time
                        ? moment(shift?.end_time).format("h:mm A")
                        : "-"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {shift?.shift_length}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {shift?.requested_by?.length === 1 ? (
                        <div className="text-sm text-gray-800">
                          {shift.requested_by[0]?.first_name}{" "}
                          {shift.requested_by[0]?.last_name}
                          {shift.requested_by[0]?.claim_type === "open_slot" &&
                            shift.requested_by[0]?.slot_start_time &&
                            shift.requested_by[0]?.slot_end_time && (
                              <div className="text-xs text-gray-500 mt-0.5">
                                Slot:{" "}
                                {moment(
                                  shift.requested_by[0].slot_start_time
                                ).format("h:mm A")}{" "}
                                –{" "}
                                {moment(
                                  shift.requested_by[0].slot_end_time
                                ).format("h:mm A")}
                              </div>
                            )}
                        </div>
                      ) : (
                        <select
                          className="shadow-sm border mt-1 block w-full py-2 text-sm border-gray-300 focus:outline-none focus:bg-[#4b6bb710] focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                          value={selectedRequestUuid[shiftId] || ""}
                          onChange={(e) =>
                            handleWorkerSelect(shiftId, e.target.value)
                          }
                        >
                          <option value="">
                            Select Worker ({shift?.requested_by?.length || 0})
                          </option>
                          {shift?.requested_by?.map((worker) => {
                            const slotLabel =
                              worker?.claim_type === "open_slot" &&
                              worker?.slot_start_time &&
                              worker?.slot_end_time
                                ? ` (${moment(worker.slot_start_time).format(
                                    "h:mm A"
                                  )} – ${moment(worker.slot_end_time).format(
                                    "h:mm A"
                                  )})`
                                : "";
                            return (
                              <option key={worker?.uuid} value={worker?.uuid}>
                                {worker?.first_name} {worker?.last_name}
                                {slotLabel}
                              </option>
                            );
                          })}
                        </select>
                      )}
                    </td>
                    <td className="px-2 py-3 w-42 min-w-[148px] max-w-[148px]"
>
                      <CustomButton
                        showLoading={approvingShiftId === shiftId}
                        label="Approve"
                        isDisabled={
                          (!selectedRequestUuid[shiftId] &&
                            shift?.requested_by?.length !== 1) ||
                          isLoading ||
                          isUserGracePeriodOver ||
                          approvingShiftId !== null
                        }
                        clickHandler={() => handleApprove(shift)}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <div className="px-2 py-4">
        {pagination?.total > 0 && (
          <PaginationComponent
            page={pagination?.current_page}
            activeListLength={shifts}
            limit={pagination?.per_page}
            totalList={pagination?.total}
            handlePagination={(newPage) => setPage(newPage)}
            totalPages={pagination?.last_page}
          />
        )}
      </div>
    </div>
  );

  return (
    <div>
      {isShow && (
        <ModalSkeleton
          title="Open Shifts Requests"
          isOpen={isShow}
          cssClass="w-[60%]"
          bodyPadding="px-0"
          closeModal={handleClose}
        >
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <CircularLoader classloader="loader-l" />
            </div>
          ) : (
            renderTableContent()
          )}
        </ModalSkeleton>
      )}
    </div>
  );
};

export default OpenShiftModal;
